import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, Nav, Pagination, Row, Spinner, Tab } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import Flag from 'react-world-flags';
import { getURLbyEndPointV2 } from '../../../../../store/api';
import { countryNameToCode } from '../../../../../store/countryCode';

const ParticipateCountry = () => {
  const [activeTab, setActiveTab] = useState('countries');
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const countriesPerPage = 6;

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getURLbyEndPointV2("getParticipateCountries"));
      const sortedCountries = response.data.data.sort((a, b) => {
        return a.country.toLowerCase().localeCompare(b.country.toLowerCase());
      });
      setCountries(sortedCountries);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (countryData) => {
    setSelectedCountry(countryData);
    setSelectedCompany(countryData.details[0]);
    setActiveTab('countryDetails');
  };

  const handleDropdownSelect = (countryId) => {
    const countryData = countries.find(c => c._id === countryId);
    if (countryData) {
      handleCountrySelect(countryData);
    }
  };

  // Placeholder for fetching registration details (implement as needed)
  const fetchRegistrationDetails = async (country, company) => {
    try {
      // Example API call (replace with actual endpoint)
      const response = await axios.get(`${getURLbyEndPointV2("getRegistrationDetails")}/${company._id}`);
      console.log("Registration details:", response.data);
      // Add logic to set slots or other data if needed
    } catch (error) {
      console.error("Error fetching registration details:", error);
    }
  };

  const renderCountriesList = () => {
    const indexOfLastCountry = currentPage * countriesPerPage;
    const indexOfFirstCountry = indexOfLastCountry - countriesPerPage;
    const currentCountries = countries.slice(indexOfFirstCountry, indexOfLastCountry);

    return (
      <>
        <Row className="mb-4">
          <Col md={6}>
            <Form.Select
              onChange={(e) => handleDropdownSelect(e.target.value)}
              defaultValue=""
            >
              <option value="" disabled>Select a country</option>
              {countries.map((country) => (
                <option key={country._id} value={country._id}>
                  {country.country}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row className="justify-content-center g-4">
          {loading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            currentCountries.map((countryData) => {
              return (
                <Col key={countryData._id} xs={12} sm={6} md={4} lg={4}>
                  <Card
                    className="h-100 cursor-pointer border-1 shadow-sm"
                    onClick={() => handleCountrySelect(countryData)}
                  >
                    <Card.Body className="text-center">
                      <div className="d-flex gap-1 justify-content-center align-items-center">
                        <Flag
                          code={countryNameToCode[countryData.country]}
                          style={{ width: '80px', height: '40px', objectFit: 'cover' }}
                          className='border'
                        />
                      </div>
                      <Card.Title className="fw-semibold mt-2 fs-6 mb-0">
                        {countryData.country}
                      </Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })
          )}

          {countries.length > countriesPerPage && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.Prev
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                />
                {Array.from({ length: Math.ceil(countries.length / countriesPerPage) }).map((_, idx) => (
                  <Pagination.Item
                    key={idx}
                    active={currentPage === idx + 1}
                    onClick={() => setCurrentPage(idx + 1)}
                  >
                    {idx + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(countries.length / countriesPerPage), p + 1))}
                  disabled={currentPage === Math.ceil(countries.length / countriesPerPage)}
                />
              </Pagination>
            </div>
          )}
        </Row>
      </>
    );
  };

  const renderCountryDetails = () => {
    if (!selectedCountry || !selectedCompany) return null;

    return (
      <Container className="p-4">
        <div className="d-flex gap-1 justify-content-between align-items-center">
          <div className="d-flex gap-1 justify-content-center align-items-center">
            <Flag
              code={countryNameToCode[selectedCountry.country]}
              className="mb-3"
              style={{ width: '100px', height: '50px' }}
            />
            <h5 className="fw-bold">{selectedCountry.country}</h5>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setActiveTab('countries')}
            className="mb-4"
          >
            <FaArrowLeft className="me-2" /> Back to Countries
          </Button>
        </div>
        <hr />
        {/* <Badge bg="primary" className='my-1'>{selectedCountry.details.length} Collaboration available</Badge> */}
        {selectedCountry.details.length > 1 && (
          <Nav variant="pills" activeKey={selectedCompany?._id} className="mb-4">
            {selectedCountry.details.map((company) => (
              <Nav.Item key={company._id} className='me-2'>
                <Nav.Link
                  eventKey={company._id}
                  onClick={() => {
                    setSelectedCompany(company);
                    fetchRegistrationDetails(selectedCountry.country, company);
                  }}
                  style={{
                    backgroundColor: selectedCompany?._id === company._id ? '#007bff' : '#f4f4f9',
                    color: selectedCompany?._id === company._id ? '#fff' : '#495057',
                    borderRadius: '5px',
                    padding: '10px 15px',
                    cursor: 'pointer',
                  }}
                >
                  {company.companyName}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

<div className="mb-4">
  <div className="d-flex align-items-center gap-3">
    {selectedCompany?.logoUrl && (
      <img
        src={selectedCompany.logoUrl}
        alt={`${selectedCompany.companyName} logo`}
        className='border'
        style={{
          width: '150px', // Adjust size as needed
          height: '150px',
          objectFit: 'contain', // Ensures the image fits nicely
        }}
      />
    )}
    <div>

    <h3 className="fw-bold">{selectedCompany?.companyName}</h3>
    <h6
            className="text-muted"
            dangerouslySetInnerHTML={{
              __html: (selectedCompany?.heading || '').replace(/\r\n/g, '<br />'),
            }}
            ></h6>
            </div>
  </div>
  <hr />
          <h5 className="mb-3">About</h5>

          <p
            className="text-muted text-justify"
            dangerouslySetInnerHTML={{
              __html: (selectedCompany?.description || '').replace(/\r\n/g, '<br />'),
            }}
          ></p>
        </div>
      </Container>
    );
  };

  return (
    <Container className="py-4">
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Tab.Content>
          <Tab.Pane eventKey="countries">
            {renderCountriesList()}
          </Tab.Pane>
          <Tab.Pane eventKey="countryDetails">
            {renderCountryDetails()}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default ParticipateCountry;